import React from 'react';
import { T } from '@transifex/react';
import { Button } from '@/components/ui/button';
import idPortenLogo from './idporten-logo.svg';
import { useIdPortenRedirect } from './useIdPortenRedirect';

/**
 * Translator instruction carried into Transifex with the source string. The
 * Norwegian wording is the captain's decision and Digdir's terms, not a
 * preference: "ID-porten" is a product name and must survive every locale
 * untranslated, and no eID vendor may be named in its place.
 */
const IDPORTEN_LABEL_COMMENT =
  'Norwegian (nb) must read exactly "Logg inn med ID-porten". ' +
  'Never translate or replace the product name "ID-porten", and never ' +
  'substitute an eID vendor name (BankID, Buypass, Commfides, MinID) — ' +
  "Digdir's terms of use forbid it.";

/**
 * ID-porten login surface — Digdir's Norwegian public-sector eID gateway
 * (Avfall Sør, `avfall.wastehero.io`).
 *
 * Rendered by UserStoreProvider when `company.login_method === 'idporten'`
 * (also reachable via the dev-pill "Login method" override), and by
 * Property.store's `pre_auth_search` path once an address is chosen.
 *
 * The backend surfaces the authorize URL in `company.authentication_urls` with
 * `provider: 'idporten'`, pointing at the platform's own OIDC start endpoint
 * (`https://<platform-host>/idporten/auth/start/`). The query contract is the
 * one every eID surface here shares, so this reuses `buildEidRedirectUrl`
 * unchanged: the live anonymous session token goes out as `api_key`, the
 * portal's own URL as `redirect_url`, and the chosen address as `property_id`.
 * ID-porten hands the citizen back to `<redirect_url>/login?api_key=…` with a
 * fresh key that upgrades the session (and `&property_key=` when a property was
 * pre-selected), or to the same place with `?error_code=` on refusal — see
 * `getErrorJsx` in src/app/User.store.tsx.
 *
 * The `api_key` is deliberately the *live* session token read at click time,
 * never a fixed credential: a hardcoded key would be a tenant secret in source
 * and would also go stale.
 *
 * ## Branding is a contract term, not a style choice
 *
 * Digdir's "Bruksvilkår for offentlege kundar" are explicit:
 * *"Kunde skal bruke ID-portens namn og logo på alle nettsider og appar.
 * e-ID-leverandørane sine namn og logo skal ikkje brukast."*
 *
 * So this surface shows **one** button reading "Logg inn med ID-porten" with
 * the official ID-porten logo, and **never** names or draws BankID, Buypass,
 * Commfides or MinID — even though those are the eIDs behind it. Choosing
 * between them is ID-porten's own screen, not ours. Do not add per-eID buttons
 * here, and do not route an ID-porten tenant through `CriptioLogin`, whose
 * unmatched-provider fallback paints Danish NemID branding.
 *
 * The logo (`./idporten-logo.svg`) is the unaltered official mark, converted
 * with `pdftocairo -svg` — a format conversion only — from
 * `02. Felleslosninger/ID-porten.pdf` in Digdir's own "Logoarkiv (For skjerm)"
 * — see `docs/auth/idporten-logo.md` for provenance and the usage
 * rules that come with it. The button keeps a light ground because Digdir
 * pairs the positive (blue-black) mark with a light background; it does not
 * take the tenant accent, which would put the mark on an unpredictable colour.
 */
export const IdPortenLogin: React.FC<{
  /** Ran just before the redirect — the Property.store "no access" screen uses
   *  it to drop the refused credential, mirroring `CriptioLogin`'s `onClick`. */
  onClick?: () => void;
}> = ({ onClick }) => {
  const { hasIdPortenUrl, redirectToIdPorten } = useIdPortenRedirect();

  // Tenant is flagged for ID-porten but the backend has no IdPortenApplication
  // row yet (the row IS the activation), so there is nothing to redirect to.
  // Say so rather than render a dead button.
  if (!hasIdPortenUrl) {
    return (
      <div className="flex w-full flex-col gap-4">
        <Button type="button" disabled className="h-12 w-full">
          <T _str="Log in with ID-porten" _comment={IDPORTEN_LABEL_COMMENT} />
        </Button>
        <p className="text-center text-sm text-muted-foreground">
          <T _str="ID-porten sign-in is not available for this municipality yet." />
        </p>
      </div>
    );
  }

  // Deliberately ONE button, even if the backend ever emitted more than one
  // entry: ID-porten is a single gateway and picking between the eIDs behind it
  // is its own screen, not ours.
  return (
    <div className="flex w-full flex-col gap-4">
      {/* `#1e2b3c` is Digdir's own blue-black, taken from the mark. Third-party
          brand colours deliberately sit outside `tokens.css` (the BankID and
          MitID buttons in CriptioLogin do the same): a tenant re-theming its
          accent must not repaint someone else's logo. */}
      <Button
        type="button"
        onClick={() => {
          onClick?.();
          redirectToIdPorten();
        }}
        className="h-12 w-full gap-3 border border-[#1e2b3c]/15 bg-white text-[#1e2b3c] hover:bg-white/90"
      >
        <img
          src={idPortenLogo}
          alt=""
          aria-hidden="true"
          className="h-6 w-auto"
        />
        <span className="text-base font-medium">
          <T _str="Log in with ID-porten" _comment={IDPORTEN_LABEL_COMMENT} />
        </span>
      </Button>
    </div>
  );
};
